<template>
  <vue-final-modal
    v-model="isOpen"
    classes="modal-overlay-container"
    content-class="modern-modal-content preview-modal"
    @before-close="closeModal"
  >
    <div class="modal-header">
      <div class="header-text-container">
        <div class="title-wrapper">
          <div class="title-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
          </div>
          <div>
            <h5 class="modal-title">{{ moduleData?.type === 'video' ? 'Video Preview' : 'SCORM Preview' }}</h5>
            <p class="modal-subtitle mb-0 text-muted small">{{ moduleData?.name || 'Loading...' }}</p>
          </div>
        </div>
      </div>
      <button type="button" class="close-button" @click="closeModal" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <div class="modal-body p-0 player-area bg-dark" v-if="moduleData?.type === 'video'">
       <div class="video-preview-wrapper p-4 w-100">
         <video-player
           v-if="videoOptions && videoOptions.sources[0].src"
           :options="videoOptions"
           :playsinline="true"
           :preview-mode="true"
         />
         <div v-else class="text-center text-white py-5">
            <div class="spinner-border text-info mb-3" role="status"></div>
            <p>Loading video securely...</p>
         </div>
       </div>
    </div>

    <div class="modal-body p-0 player-area bg-slate" v-else>
      <div class="scorm-preview-wrapper w-100 h-100" style="min-height: 550px;">
        <scorm-player
          v-if="isOpen && moduleData"
          assignment-id="preview"
          :module-id="moduleData.id || moduleData.ID"
          :user-id="$store.state.auth.jwtPayload.user_id"
          :launch-path="moduleData.launch_path"
        />
      </div>
    </div>

  </vue-final-modal>
</template>

<script setup>
import { computed, watch, ref, defineEmits, defineProps } from 'vue';
import VideoPlayer from "@/components/VideoPlayer.vue";
import ScormPlayer from "@/components/scorm/ScormPlayer.vue";

const props = defineProps(['modelValue', 'moduleData']);
const emit = defineEmits(['update:modelValue']);

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const videoOptions = ref(null);

// This stops SCORM packages from crashing during an admin preview
const setupMockScormAPI = () => {
  const mockAPI = {
    Initialize: function() { return "true"; },
    Terminate: function() { return "true"; },
    GetValue: function() { return ""; },
    SetValue: function() { return "true"; },
    Commit: function() { return "true"; },
    GetLastError: function() { return "0"; },
    GetErrorString: function() { return ""; },
    GetDiagnostic: function() { return ""; }
  };

  // Expose to window so the iframe can find it
  window.API = mockAPI;          // SCORM 1.2
  window.API_1484_11 = mockAPI;  // SCORM 2004
};

const initVideo = () => {
  if (!props.moduleData || props.moduleData.type !== 'video') {
    videoOptions.value = null;
    return;
  }

  const newVal = props.moduleData;
  let videoUrl = '';
  let posterUrl = '';

  if (newVal.isDefaultVideo && newVal.originalData?.training_video) {
    videoUrl = newVal.originalData.training_video.url;
    posterUrl = newVal.originalData.training_video.thumbnail;
  }
  else {
    videoUrl = (newVal.launch_path && newVal.launch_path !== '--')
                ? newVal.launch_path
                : (newVal.originalData?.gcs_path || newVal.gcs_path || '');
    posterUrl = newVal.originalData?.thumbnail || newVal.thumbnail || '';
  }

  if (videoUrl) {
    videoOptions.value = {
      fluid: true,
      autoplay: false,
      controls: true,
      muted: false,
      language: 'en',
      sources: [{ type: "video/mp4", src: videoUrl }],
      poster: posterUrl,
      playbackRates: [0.5, 1, 1.5, 2],
      controlBar: { pictureInPictureToggle: false }
    };
  } else {
    videoOptions.value = null;
  }
};

// Watch for the modal opening to trigger the correct initialization
watch(() => props.modelValue, (isModalOpen) => {
  if (isModalOpen && props.moduleData) {
    if (props.moduleData.type === 'video') {
      initVideo();
    } else {
      setupMockScormAPI(); // Inject dummy API before the SCORM player mounts!
    }
  }
}, { immediate: true });

const closeModal = () => {
  isOpen.value = false;
  setTimeout(() => {
    videoOptions.value = null;
  }, 300);
};
</script>

<style scoped>
:deep(.modal-overlay-container) { display: flex; justify-content: center; align-items: center; background-color: rgba(15, 23, 42, 0.75); backdrop-filter: blur(6px); z-index: 1000; }
:deep(.preview-modal) { background: white; border-radius: 12px; width: 95%; max-width: 900px; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); display: flex; flex-direction: column; border: none !important; padding: 0; margin: 0 1rem; overflow: hidden; }

.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; border-bottom: 1px solid #f1f5f9; background-color: #ffffff; }
.title-wrapper { display: flex; align-items: center; gap: 0.75rem; }
.title-icon { color: #00a0c1; display: flex; align-items: center; justify-content: center; background: rgba(0, 160, 193, 0.1); padding: 8px; border-radius: 8px; }
.modal-title { color: #1e293b; margin: 0; font-size: 1.15rem; font-weight: 700; line-height: 1.2;}
.close-button { background: #f1f5f9; border: none; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #94a3b8; transition: all 0.2s; }
.close-button:hover { background-color: #fee2e2; color: #dc3545; transform: rotate(90deg); }

.player-area { height: auto; min-height: 400px; display: flex; align-items: center; justify-content: center; }
.bg-slate { background-color: #0f172a; }
.bg-dark { background-color: #1e293b; }
.video-preview-wrapper { max-width: 800px; margin: 0 auto; }
</style>